import { Component, Element, Host, Listen, Prop, State, h, Fragment } from '@stencil/core';

interface GlobalState {
  muted?: boolean;
}

const globalState = ((window as any).TangiaVideoPlayerGlobal ?? {}) as GlobalState;
(window as any).TangiaVideoPlayerGlobal = globalState;

const GlobalStateEvent = 'tangia-player-global-changed';

const GlobalVideoPlayingEvent = 'tangia-player-playing';
interface GlobalVideoPlayingEventDetail {
  element: HTMLVideoElement;
}

let instanceCnt = 0;

const transparentPixel = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';
@Component({
  tag: 'tangia-video-player',
  shadow: true,
  styleUrl: 'element.css',
})
export class TangiaVideoPlayer {
  @Prop()
  preload?: string;
  @Prop({ mutable: true })
  autoplay?: boolean;
  @Prop()
  loop?: boolean;
  @Prop()
  toggleOnClick?: boolean;
  @Prop()
  src?: string;
  @Prop()
  poster?: string;
  @Prop()
  extracss?: string;
  @Prop()
  muted?: boolean;
  @Prop()
  alwaysMuted?: boolean;

  @Element() el: HTMLElement;

  // updating the prop doesn't cause a re-render so we put it in the state
  @State()
  protected isMuted = globalState.muted ?? this.muted ?? false;

  @Prop()
  videoOnHover: boolean;
  @Prop()
  videoOnVisible: boolean;
  @Prop()
  allowConcurrent: boolean;

  protected intersectionObserver?: IntersectionObserver;

  @State()
  protected playing = false;
  @State()
  protected isHovering = false;
  @State()
  protected isVisible = false;

  protected _video?: HTMLVideoElement;

  protected instanceId = instanceCnt++;

  componentWillLoad() {
    this.isMuted = this.alwaysMuted || (globalState.muted ?? this.muted ?? false);
  }

  connectedCallback() {
    if (this.videoOnVisible) {
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
      }
      this.intersectionObserver = new IntersectionObserver(
        entries => {
          //console.log('intersect', this.instanceId, entries[0].isIntersecting);
          this.isVisible = entries[0].isIntersecting;
          if (this.isVisible) {
            setTimeout(() => {
              this.play();
            }, 100);
            // we sometimes don't get the end of the intersection, so we do a force re-detect after a bit
            setTimeout(() => {
              if (!this.isVisible || !this.intersectionObserver) return;
              //console.log('running redetect', this.instanceId);
              this.intersectionObserver.unobserve(this.el);
              this.intersectionObserver.observe(this.el);
            }, 500 + Math.random() * 500);
          }
        },
        { root: window.document },
      );
      this.intersectionObserver.observe(this.el);
    }
    if (this.autoplay && !this.videoOnHover && !this.videoOnVisible) {
      // iOS is a pita...
      setTimeout(() => {
        this.play();
      }, 100);
    }
  }

  disconnectedCallback() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  }

  @Listen('mouseenter')
  handleMouseEnter() {
    if (!this.videoOnHover) return;
    this.isHovering = true;
    setTimeout(() => {
      if (!this.isHovering) return;
      if (this.playing) return;
      this.play();
    }, 50);
  }

  @Listen('mouseleave')
  handleMouseLeave() {
    if (!this.videoOnHover) return;
    this.isHovering = false;
  }

  @Listen('tangia-player-global-changed', { target: 'window' })
  handleGlobalMute() {
    if (!this.alwaysMuted) {
      this.isMuted = globalState.muted ?? this.isMuted;
    }
  }
  @Listen('tangia-player-playing', { target: 'window' })
  handleGlobalVideoPlaying(e: CustomEvent<GlobalVideoPlayingEventDetail>) {
    if (!this._video) return;
    if (this._video === e.detail.element) return;
    if (this.allowConcurrent) {
      // even if playing multiple videos is fine, playing multiple audios is just chaos
      this._video.muted = true;
      this.isMuted = true;
    } else {
      this.pause();
    }
  }

  render() {
    let showVideo: boolean | undefined = undefined;
    if (this.videoOnHover || this.videoOnVisible) {
      showVideo = false;
    }
    if (this.videoOnHover && this.isHovering) {
      showVideo = true;
    }
    if (this.videoOnVisible && this.isVisible) {
      showVideo = true;
    }
    /*
    console.log(
      'tvp',
      this.instanceId,
      'showVideo',
      showVideo,
      'videoOnHover',
      this.videoOnHover,
      'isHovering',
      this.isHovering,
      'videoOnVisible',
      this.videoOnVisible,
      'isVisible',
      this.isVisible,
      showVideo ? this.el : null,
    );
    */
    if (showVideo === false) {
      return (
        <Host class="video-poster flex flex-col h-full w-full">
          <style>{this.extracss}</style>
          <img class="grow object-contain" src={this.poster} />
        </Host>
      );
    }
    const effectivelyMuted = this.alwaysMuted || this.isMuted;
    const domExtras = {
      controlslist: 'nodownload nofullscreen noremoteplayback',
      disablePictureInPicture: true,
      disableRemotePlayback: true,
      defaultMuted: effectivelyMuted,
    };
    return (
      <Host class="video-player flex flex-col h-full w-full">
        <style>{this.extracss}</style>
        <video
          ref={el => {
            if (this._video && this._video !== el) {
              this._video.pause();
            }
            this._video = el;
            if (!el) return;
            el.muted = effectivelyMuted;
            el.defaultMuted = effectivelyMuted;
            // autoplay does not work on some browsers if video does not have `muted`
            // attribute:
            // https://developer.mozilla.org/en-US/docs/Web/HTML/Element/video#attr-autoplay

            // open bug since 2017 that you cannot set muted in video element https://github.com/facebook/react/issues/10389
            // https://github.com/facebook/react/issues/10389
            if (effectivelyMuted) el.setAttribute('muted', '');
            // iOS...
            el.setAttribute('webkit-playsinline', '');
            el.setAttribute('playsinline', '');
          }}
          class="grow object-contain"
          // no idea why this exists multiple times
          playsInline={true}
          playsinline={true}
          webkit-playsinline={true}
          onClick={e => this.togglePlay(e)}
          muted={effectivelyMuted}
          loop={this.loop}
          autoplay={this.autoplay}
          preload={this.preload}
          // prevent showing the ugly default poster while loading
          poster={this.poster || transparentPixel}
          onPlaying={() => this.play()}
          onEnded={() => this.pause()}
          {...domExtras}
        >
          {this.src?.endsWith('.webm') ? (
            <Fragment>
              <source src={this.src} type="video/webm" />
              <source src={this.src.replace(/\.webm$/, '.mp4')} type="video/mp4" />
            </Fragment>
          ) : (
            <source src={this.src} />
          )}
        </video>
        <div id="controls" class="flex shrink-0">
          {!this.playing ? (
            <button onClick={e => this.play(e)}>
              <slot key="play-button" name="play-button"></slot>
            </button>
          ) : (
            <button onClick={e => this.pause(e)}>
              <slot key="pause-button" name="pause-button"></slot>
            </button>
          )}
          <slot name="control-gap"></slot>
          {!this.alwaysMuted &&
            (!this.isMuted ? (
              <button onClick={e => this.mute(e)}>
                <slot key="mute-button" name="mute-button"></slot>
              </button>
            ) : (
              <button onClick={e => this.unmute(e)}>
                <slot key="unmute-button" name="unmute-button"></slot>
              </button>
            ))}
        </div>
      </Host>
    );
  }

  protected async play(e?: MouseEvent) {
    // console.log("play")
    e?.preventDefault();
    e?.stopImmediatePropagation();
    if (!this._video) return;
    this._video.playsInline = true;
    this.playing = true;
    // weird bug(?) in Chrome which ignores the attribute in the HTML when it's added dynamically
    // https://stackoverflow.com/a/51755171/4321944
    this._video.muted = this.isMuted;
    try {
      await this._video.play();
      window.dispatchEvent(new CustomEvent(GlobalVideoPlayingEvent, { detail: { element: this._video } } as CustomEvent<GlobalVideoPlayingEventDetail>));
    } catch {
      // workaround for mobile where we are not allowed to auto-play with sound
      this.playing = false;
      if (!this._video) return;
      this._video.muted = true;
      try {
        await this._video.play();
        setTimeout(() => {
          this._video.muted = this.muted;
        }, 100);
        window.dispatchEvent(new CustomEvent(GlobalVideoPlayingEvent, { detail: { element: this._video } } as CustomEvent<GlobalVideoPlayingEventDetail>));
      } catch (err) {
        console.error('Could not play video', err);
      }
    }
  }

  protected pause(e?: MouseEvent) {
    // console.log("pause")
    e?.preventDefault();
    e?.stopImmediatePropagation();
    if (!this._video) return;
    this.playing = false;
    this.autoplay = false;
    this._video.pause();
  }

  protected togglePlay(e?: MouseEvent) {
    if (!this.toggleOnClick) return;
    // console.log("togglePlay")
    if (this.playing) {
      this.pause(e);
    } else {
      this.play(e);
    }
  }

  protected mute(e?: MouseEvent) {
    // console.log("mute")
    e?.preventDefault();
    e?.stopImmediatePropagation();
    this.isMuted = true;
    if (this._video) {
      this._video.muted = this.isMuted;
    }
    globalState.muted = this.isMuted;
    window.dispatchEvent(new CustomEvent(GlobalStateEvent));
  }

  protected unmute(e?: MouseEvent) {
    // console.log("unmute", this._video, this.mute)
    e?.preventDefault();
    e?.stopImmediatePropagation();
    this.isMuted = false;
    if (this._video) {
      this._video.muted = this.isMuted;
    }
    globalState.muted = this.isMuted;
    window.dispatchEvent(new CustomEvent(GlobalStateEvent));
  }
}
